import { EMR_APIS } from "@/api_framework/api_config";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback } from "react";

import AxiosCurlirize from "axios-curlirize";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";
import { IVoiceEMRItems, IVoiceEMRText } from "./useVoiceEMRResultAPI";

AxiosCurlirize(axios, () => {});

/**
 * Lifecycle of a single voice recording, mirrored from the backend.
 *
 *   in_progress           audio is streaming, or the socket dropped mid-recording
 *   ready_for_processing  audio saved; nobody has asked to generate yet
 *   processing            a generate call is actually running
 *   completed             both halves saved and available
 *
 * The first three all mean "there is a recording to come back to", so the prompt
 * treats them identically — only `completed` offers the previous result.
 */
export type VoiceSessionStatus = "in_progress" | "ready_for_processing" | "processing" | "completed";

export interface IVoiceEMRSessionState {
  voice_id: string;
  status: VoiceSessionStatus;
  transcript: string;
  segment_count: number;
  audio_duration_seconds: number | null;
  updated_at: string;
  /** Only populated when `status` is `completed`. */
  emr_data: (IVoiceEMRText & IVoiceEMRItems) | null;
}

/**
 * Recording state for an EMR, used to decide which prompt to show before a
 * doctor starts recording.
 *
 * Resolves to `null` when the backend answers 204 — nothing has been recorded
 * for this EMR, so recording should just start without asking anything.
 */
export const useVoiceEMRSessionAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(
    async (emrId: string): Promise<IVoiceEMRSessionState | null> => {
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const url = `${EMR_APIS?.VOICE_EMR_SESSION?.baseURL}?emr_id=${encodeURIComponent(emrId)}`;

        return await axios
          .get(url, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
            // This lookup sits between the mic tap and the socket dial, so a hung
            // request holds up the whole start. Failing it is harmless — the catch
            // below treats that as "nothing to resume" and recording starts fresh.
            timeout: 8000,
          })
          .then((res: AxiosResponse<{ data?: IVoiceEMRSessionState }>) => {
            // 204 carries no body — nothing recorded yet.
            if (res.status === 204) return null;
            return res.data?.data ?? null;
          })
          .catch((e: AxiosError) => {
            captureError(e);
            sendCurl(e.config?.["curlCommand"]);
            // Treated as "nothing to resume" on purpose: a failed state lookup
            // must not block the doctor from recording.
            return null;
          });
      } catch {
        return null;
      }
    },
    [configuration, captureError, sendCurl],
  );

  return { execute };
};
